import {
  AVATAR_DEFAULT_NAME,
  MANUAL_MODE_ACTIVE_THRESHOLD,
  MANUAL_STYLE_CHANGED_PROMPT,
} from 'components/AiMode/constants';
import { dropdownOptions, spotterLink } from 'components/LeftPanel/Header/constants';
import { MessageRoleType, MessageType, SupportedUploadMimeType, TsCssVariable } from 'types';

export const isJSON = (str: string): boolean => {
  if (!str) return false;
  const trimmed = str.trim();
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
    return false;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch (e) {
    return false;
  }
};

export const formatJSON = (jsonString: string): string => {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

export const getIsAiResponseIssue = (id: string): boolean => {
  const messageId = id.split('-')[0];
  return messageId === MessageRoleType.ISSUE;
};

export const getIsLastUserMessage = (currentIndex: number, totalLength: number): boolean => {
  return currentIndex === totalLength - 2;
};

export const getIsLastAiMessage = (currentIndex: number, totalLength: number): boolean => {
  return currentIndex === totalLength - 1;
};

export const extractJsonFromReadme = (
  readme: string
): Partial<Record<TsCssVariable, string>> | null => {
  const json = readme.match(/```json\n(.*?)\n```/s);
  if (!json) return null;
  try {
    const parsedJson = JSON.parse(json[1]);
    if (parsedJson?.customizations?.style?.customCSS?.variables) {
      return parsedJson.customizations.style.customCSS.variables;
    }
  } catch {
    return null;
  }
  return null;
};

export const getIsManualModeActive = (messages: MessageType[]): boolean => {
  let count = 0;
  for (const message of messages) {
    if (message.role === MessageRoleType.AI && !extractJsonFromReadme(message.content)) {
      count++;
    }
  }
  return count >= MANUAL_MODE_ACTIVE_THRESHOLD;
};

export const getAiChatContent = ({
  styleStateKey,
  message,
}: {
  styleStateKey: string;
  message: MessageType;
}) => {
  const userName = styleStateKey.split('-')[1] || AVATAR_DEFAULT_NAME;
  const totalFiles = (message?.images?.length || 0) + (message?.pdfFiles?.length || 0);
  const hasMoreFiles = totalFiles > 1;
  const remainingFilesCount = totalFiles - 1;
  const isAiResponseIssue = getIsAiResponseIssue(message.id);

  return {
    userName,
    totalFiles,
    hasMoreFiles,
    remainingFilesCount,
    isAiResponseIssue,
  };
};

export const getSupportedUploadMimeType = (): string => {
  return Object.values(SupportedUploadMimeType).join(', ') || '';
};

export const getChatMessagesKey = (styleStateKey: string): string => {
  return (
    'chatMessages' +
    (styleStateKey ? `-${styleStateKey.split('-')[1]}-${styleStateKey.split('-')[2]}` : '')
  );
};

/**
 * Builds the prompt string to notify the AI about manual CSS variable overrides made by the user.
 * @param manualOverrides - CSS variables that were manually changed since the last AI response.
 * @param userContent - The user's original chat message.
 * @returns The formatted prompt string with overrides and user content injected.
 */
export const getManualStyleChangedPrompt = (
  manualOverrides: Partial<Record<TsCssVariable, string>>,
  userContent: string
): string => {
  const manualOverridesString = JSON.stringify(manualOverrides, null, 2);
  const userContentString = userContent.trim();
  return MANUAL_STYLE_CHANGED_PROMPT.replace('{{manualOverrides}}', manualOverridesString)
    .replace('{{userContent}}', userContentString)
    .trim();
};

/**
 * Compares the current style state against the last AI-applied variables to detect manual user changes.
 * @param styleState - The current CSS variable values in the app.
 * @param lastAiMessages - The CSS variable values from the last AI response.
 * @returns An object containing only the changed variables, or null if nothing changed.
 */
export const getIsManualStyleChanged = (
  styleState: Partial<Record<TsCssVariable, string>>,
  lastAiMessages: Record<string, string>
) => {
  const changedStyleState = {};
  let isUpdated = false;
  for (const key in styleState) {
    if (!lastAiMessages[key]) {
      changedStyleState[key] = styleState[key];
      isUpdated = true;
    } else if (lastAiMessages[key] !== styleState[key]) {
      changedStyleState[key] = styleState[key];
      isUpdated = true;
    }
  }
  return isUpdated ? changedStyleState : null;
};

/**
 * Appends the user message to the chat history, enriching it with any
 * manual CSS overrides the user made since the last AI response.
 */
export const filterApiMessages = (
  messages: MessageType[],
  styleState: Partial<Record<TsCssVariable, string>>,
  userMessage: MessageType
): MessageType[] => {
  const lastAiMsg = [...messages]
    .reverse()
    .find((m) => m.role === MessageRoleType.AI && extractJsonFromReadme(m.content));
  const lastAiVars = lastAiMsg ? extractJsonFromReadme(lastAiMsg.content) : null;

  const overridesStyle = lastAiVars && getIsManualStyleChanged(styleState, lastAiVars);
  const newUserMessage = overridesStyle
    ? { ...userMessage, content: getManualStyleChangedPrompt(overridesStyle, userMessage.content) }
    : userMessage;

  return [...messages, newUserMessage];
};

export const getMessageRef = (
  index: number,
  totalLength: number,
  lastUserMsgRef: React.Ref<HTMLDivElement>,
  lastAiMsgRef: React.Ref<HTMLDivElement>
): React.Ref<HTMLDivElement> | undefined => {
  return index === totalLength - 2
    ? lastUserMsgRef
    : index === totalLength - 1
    ? lastAiMsgRef
    : undefined;
};

/**
 * Returns the label for a given id from the dropdown options or spotter link.
 * @param id - The id to get the label for.
 * @returns The label for the given id.
 */
export const getLabelFromId = (id: string) => {
  const links = [...dropdownOptions, ...spotterLink];
  return links.find((option) => option.id === id)?.label || id;
};
